import { apiClient } from '../../utils/apiClient';
import {
    API_PROFILE_PICTURE_UPLOAD,
    API_PROFILE_PICTURE_DELETE,
    API_PROFILE_PICTURE_SELECT_DEFAULT,
    API_PROFILE_PICTURE_DEFAULTS,
    API_ADMIN_PROFILE_PICTURE_DEFAULTS,
    API_ADMIN_PROFILE_PICTURE_DEFAULTS_UPDATE,
    API_ADMIN_PROFILE_PICTURE_DEFAULTS_DELETE,
} from '../../constants/apiConstants';

export interface DefaultAvatarDto {
    id: number;
    name: string;
    url: string;
}

export interface DefaultAvatarAdminDto {
    id: number;
    name: string;
    blobKey: string;
    blobUrl: string;
    sortOrder: number;
    isActive: boolean;
    createdAt: string | null;
    modifiedAt: string | null;
}

export interface UploadProfilePictureResult {
    profilePictureUrl: string;
}

export interface SelectDefaultAvatarResult {
    profilePictureUrl: string;
}

// ── User-facing API ───────────────────────────────────────────────────────────

export const uploadProfilePicture = async (file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post(API_PROFILE_PICTURE_UPLOAD, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Upload failed');
    }
    return result.data?.profilePictureUrl ?? '';
};

export const selectDefaultAvatar = async (defaultAvatarId: number): Promise<string> => {
    const response = await apiClient.put(API_PROFILE_PICTURE_SELECT_DEFAULT, { defaultAvatarId });
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Select avatar failed');
    }
    return result.data?.profilePictureUrl ?? '';
};

export const deleteProfilePicture = async (): Promise<void> => {
    const response = await apiClient.delete(API_PROFILE_PICTURE_DELETE);
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Delete failed');
    }
};

export const listDefaultAvatars = async (): Promise<DefaultAvatarDto[]> => {
    const response = await apiClient.get(API_PROFILE_PICTURE_DEFAULTS);
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to fetch default avatars');
    }
    return result.data?.avatars ?? [];
};

// ── Admin API ─────────────────────────────────────────────────────────────────

export const adminListDefaultAvatars = async (): Promise<DefaultAvatarAdminDto[]> => {
    const response = await apiClient.get(API_ADMIN_PROFILE_PICTURE_DEFAULTS);
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Failed to fetch default avatars');
    }
    return result.data?.avatars ?? [];
};

export const adminUploadDefaultAvatar = async (name: string, file: File): Promise<DefaultAvatarAdminDto> => {
    const form = new FormData();
    form.append('name', name);
    form.append('file', file);
    const response = await apiClient.post(API_ADMIN_PROFILE_PICTURE_DEFAULTS, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Upload failed');
    }
    const avatar = result.data?.avatar;
    if (!avatar) throw new Error('Missing avatar data in response');
    return avatar;
};

export const adminUpdateDefaultAvatar = async (
    id: number,
    patch: { name?: string; sortOrder?: number; isActive?: boolean }
): Promise<DefaultAvatarAdminDto> => {
    const response = await apiClient.put(API_ADMIN_PROFILE_PICTURE_DEFAULTS_UPDATE(id), patch);
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Update failed');
    }
    const avatar = result.data?.avatar;
    if (!avatar) throw new Error('Missing avatar data in response');
    return avatar;
};

export const adminDeleteDefaultAvatar = async (id: number): Promise<void> => {
    const response = await apiClient.delete(API_ADMIN_PROFILE_PICTURE_DEFAULTS_DELETE(id));
    const result = response.data;
    if (!result.succeeded) {
        throw new Error(result.messages?.join(', ') ?? 'Delete failed');
    }
};
